import React from 'react';
import Select from 'react-select';
import MultiDropdownGrouped from 'ats/components/multi-dropdown-grouped/MultiDropdownGrouped';
import { applicantStore } from 'ats/pages/applicants/ApplicantStore';
import UserProfileAvatar from 'ats/components/user-profile-avatar/UserProfileAvatar';
import { reactSelectAtsComponents } from 'ats/components/react-select/reactSelectAtsComponents';

export class ApplicantDropdown extends React.Component {
	state = {
		loaded: false,
		inputValue: '',
		options: [],
		page: 1,
		hasMore: false,
		isLoading: false
	};

	MIN_SEARCH_CHARS = 3;
	PAGE_SIZE = 20;
	MAX_OPTIONS = 100;

	async componentDidMount() {
		this.setState({ loaded: true });
	}

	render() {
		if (this.state.loaded) {
			const { isMulti, placeholder = 'Applicant' } = this.props;
			// Multi-select uses the grouped wrapper so currently selected
			// applicants are pinned at the top and the count pill is rendered.
			const Component = isMulti ? MultiDropdownGrouped : Select;

			return (
				<Component
					menuPosition={this.props.menuPosition || 'fixed'}
					menuPlacement={this.props.menuPlacement || 'auto'}
					closeMenuOnScroll={this.props.closeMenuOnScroll}
					menuPortalTarget={this.props.menuPortalTarget}
					maxMenuHeight={300}
					classNamePrefix={
						this.props.classNamePrefix ||
						'react-select' +
							(this.props.styleVersion ? `-${this.props.styleVersion}` : '')
					}
					className={
						'react-select ' +
						(isMulti ? 'ats-multi-dropdown ats-multi-dropdown-grouped ' : '') +
						(this.props.styleVersion ? this.props.styleVersion : '') +
						(this.props.isInvalid ? ' has-error' : '')
					}
					isMulti={isMulti}
					value={this.props.value}
					placeholder={placeholder}
					options={this.state.options}
					components={isMulti ? undefined : reactSelectAtsComponents}
					styles={{
						container: (base, state) => ({
							...base,
							...(state.isDisabled && {
								opacity: 0.5,
								cursor: 'not-allowed'
							})
						}),
						control: base => ({
							...base,
							paddingRight: 12,
							...(this.props.customStyles?.control || {})
						})
					}}
					isClearable={this.props.isClearable ?? true}
					isDisabled={this.props.disabled}
					isLoading={this.state.isLoading}
					inputValue={this.state.inputValue}
					onInputChange={this.handleInputChange}
					getOptionValue={this.getOptionValue}
					getOptionLabel={this.getOptionLabel}
					formatOptionLabel={this.formatOptionLabel}
					noOptionsMessage={this.noOptionsMessage}
					onMenuScrollToBottom={this.handleMenuScrollToBottom}
					onChange={item => {
						this.setState({ inputValue: '' });
						if (this.props.onValueChange) {
							this.props.onValueChange(item);
						}
					}}
					{...this.props.AsyncProps}
				/>
			);
		} else {
			return null;
		}
	}

	handleInputChange = (newValue, { action }) => {
		if (action !== 'input-change') return newValue;

		const inputValue = (newValue || '').trimStart();
		this.setState(
			{
				inputValue,
				page: 1,
				hasMore: false,
				options: []
			},
			() => {
				this.debouncedSearch(inputValue);
			}
		);

		return inputValue;
	};

	debouncedSearchTimer = null;

	debouncedSearch = inputValue => {
		if (this.debouncedSearchTimer) {
			clearTimeout(this.debouncedSearchTimer);
		}

		this.debouncedSearchTimer = setTimeout(() => {
			this.searchFirstPage(inputValue);
		}, 250);
	};

	isValidSearchQuery = q => {
		if (!q) return false;
		if (/^\d+$/.test(q)) return true;
		return q.length >= this.MIN_SEARCH_CHARS;
	};

	searchFirstPage = async inputValue => {
		const q = (inputValue || '').trim();
		if (!this.isValidSearchQuery(q)) {
			this.setState({ options: [], hasMore: false, isLoading: false, page: 1 });
			return;
		}

		this.setState({ isLoading: true });
		try {
			const rows = await this.getApplicants({
				text: q,
				pagination: { page: 1, limit: this.PAGE_SIZE }
			});

			const selected = this.props.value
				? Array.isArray(this.props.value)
					? this.props.value
					: [this.props.value]
				: [];
			const byId = new Map();
			[...selected, ...rows].forEach(r => {
				if (r && r.id != null) byId.set(r.id, r);
			});

			const nextOptions = Array.from(byId.values()).slice(0, this.MAX_OPTIONS);

			this.setState({
				options: nextOptions,
				page: 1,
				hasMore:
					rows.length === this.PAGE_SIZE &&
					nextOptions.length < this.MAX_OPTIONS,
				isLoading: false
			});
		} catch (e) {
			this.setState({ isLoading: false, options: [], hasMore: false, page: 1 });
		}
	};

	handleMenuScrollToBottom = async () => {
		const q = (this.state.inputValue || '').trim();
		if (this.state.isLoading) return;
		if (!this.state.hasMore) return;
		if (!this.isValidSearchQuery(q)) return;

		const nextPage = this.state.page + 1;
		this.setState({ isLoading: true });
		try {
			const rows = await this.getApplicants({
				text: q,
				pagination: { page: nextPage, limit: this.PAGE_SIZE }
			});

			const byId = new Map();
			[...this.state.options, ...rows].forEach(r => {
				if (r && r.id != null) byId.set(r.id, r);
			});

			const nextOptions = Array.from(byId.values()).slice(0, this.MAX_OPTIONS);

			this.setState({
				options: nextOptions,
				page: nextPage,
				hasMore:
					rows.length === this.PAGE_SIZE &&
					nextOptions.length < this.MAX_OPTIONS,
				isLoading: false
			});
		} catch (e) {
			this.setState({ isLoading: false });
		}
	};

	getApplicants = async inputValue => {
		const result = await applicantStore.getApplicants({
			...inputValue,
			jobId: this.props.filterByJobId,
			...(this.props.hasPublicProfile ? { hasPublicProfile: true } : {}),
			...(this.props.excludeApplicantId != null
				? { excludeId: this.props.excludeApplicantId }
				: {})
		});
		return result.data.rows;
	};

	noOptionsMessage = value => {
		const q = value.inputValue?.trim() || '';
		if (this.isValidSearchQuery(q)) {
			return 'No matches found';
		}
		return `Type at least ${this.MIN_SEARCH_CHARS} characters (or an ID) to search candidates`;
	};

	getOptionValue = option => option.id;

	getOptionLabel = option => {
		const name = option?.name || '';
		if (this.props.showId && option?.id != null) {
			return name ? `#${option.id} - ${name}` : `#${option.id}`;
		}
		return name;
	};

	formatOptionLabel = option => {
		const label = this.getOptionLabel(option);
		if (!this.props.showAvatar) return label;

		return (
			<UserProfileAvatar
				imageUrl={option?.imageUrl || option?.candidate?.imageUrl}
				name={label}
				showName
			/>
		);
	};
}
